import Heart from '@components/icons/Heart'
import HeartSelected from '@components/icons/HeartSelected'
import { useAddToCart } from '@pylot-data/hooks/cartManager/use-add-to-cart'
import { usePrice } from '@corratech/pylot-price'
import { useDataLayerAction } from '@corratech/pylot-tag-manager'
import { Button } from '@corratech/pylot-ui'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { isGiftCardMixCartResult } from '@corratech/pylot-cart-manager'
import {
    DisabledValues,
    getDisabledOptionValues
} from '@corratech/pylot-variant-selector/src/utils/getDisabledOptionValues'
import { formatCurrency } from '@lib/utils/formatCurrency'

import { AddToWishlist } from '@corratech/pylot-wishlist'
import { useElementOnScreen } from '@lib/hooks/useElementOnScreen'
import { useIsMobileScreen, useIsTabletScreen } from '@lib/utils/mediaQuery'
import {
    getVariantBySelectedOptions,
    useProductUI
} from '@pylot-data/hooks/product/use-product-ui'
import {
    CategoryTree,
    ConfigurableAttributeOption,
    ConfigurableProduct,
    ConfigurableProductOptions,
    ConfigurableVariant,
    Maybe,
    ProductStockStatus
} from '@pylot-data/pylotschema'
import { BundleItem } from '@pylot-data/fwrdschema'
import cn from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { rememberPlpProductClick } from '@lib/hooks/cagegory/plp/usePLPScrollRestore'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import ConfigurableAddToCart from '../ConfigurableAddToCart'
import { ProductPrice } from '../ProductPrice/ProductPrice'
import s from './ProductTile.module.scss'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { ProductType } from '@pylot-data/hooks/product/use-product-fetch'
import { ReviewStars } from '@components/Reviews/ReviewStars'
import {
    SelectedOptions,
    VariantSelector
} from '@corratech/pylot-variant-selector'
import { useScrollPosition } from '@lib/hooks/useScrollPosition'

const categoryUrlSuffix = '.html'

const NEW_FLAG_STRING = 'NUEVO'

export interface ProductTileProps {
    product: any
    imageParam: {
        width?: number
    }
    className?: string
    isPDP?: boolean
    isFBT?: boolean
    handleChangeItemOption?: any
    category?: Maybe<CategoryTree>
}

export interface Attribute {
    __typename: string
    code: string
    value_index: number
    label: string
}

export interface SmallImage {
    __typename: string
    url: string
}

export interface VariantProduct {
    __typename: string
    id: number
    small_image: SmallImage
    sku: string
    stock_status: string
}

export interface Variant {
    __typename: string
    attributes: Attribute[]
    product: VariantProduct
}

export { LoaderTile } from './LoaderTile'

const renderWishListButtonContent = () => {
    return function WishlistButtonContent({
        isProductInWishlist
    }: {
        isProductInWishlist: boolean
    }) {
        return (
            <div className={s['add-to-wishlist-btn']}>
                {isProductInWishlist ? <HeartSelected /> : <Heart />}
            </div>
        )
    }
}

const fullImageUrl = (
    imageUrl?: string,
    imageParam?: {
        width?: number
    }
): string => {
    const widthString = imageParam?.width ? `&width=${imageParam.width}` : ''
    return `${imageUrl}?auto=webp&format=pjpg${widthString}`
}

export const FwrdProductTile = ({
    product = {},
    imageParam = {},
    className,
    isPDP = false,
    isFBT = false,
    category,
    handleChangeItemOption = {}
}: ProductTileProps): ReactElement | null => {
    const {
        url_key,
        media_gallery,
        badge_image_text: badgeImageText,
        badge_caption_icon: badgeCaptionIcon,
        price_range: productPriceRange,
        customTextAfterProductImage,
        flag,
        new_to_date: newToDate,
        rating_summary,
        review_count
    } = product

    const { t } = useTranslation(['pdp', 'common', 'wishlist'])
    const [hoveredInstantAdd, setHoveredInstantAdd] = useState(false)
    const [isAddToCartDisabled, setIsAddToCartDisabled] = useState(false)
    const [disabledOptions, setDisabledOptions] =
        useState<DisabledValues | null>(null)
    const isMobile = useIsMobileScreen()
    const isTablet = useIsTabletScreen()
    const { openSidebar, openCartToast, openToast } = useUI()
    const { addToCart } = useAddToCart()
    const { total: productTotalPrice } = usePrice(product.price_range)
    const dataLayerAction = useDataLayerAction()
    const search =
        typeof window !== 'undefined' && !isPDP
            ? new URL(window.location.href).search
            : ''

    const [elementRef, isVisible] = useElementOnScreen()
    const isConfig = product.__typename === 'ConfigurableProduct'
    const isBundle = product.__typename === 'BundleProduct'

    const productUrl = `/producto/${url_key}${categoryUrlSuffix}${
        isConfig ? search : ''
    }`

    const sizeCategoryOption: ConfigurableProductOptions | null =
        product.configurable_options?.find(
            (el: { attribute_code: string }) => el.attribute_code === 'talla'
        ) || null

    // TODO: make dynamic?
    const swatchAttr = 'color'

    const {
        setSelectedOptions,
        selectedOptions,
        options,
        variant,
        selectedOptionUIDs,
        isConfigOptionSelected
    } = useProductUI(product)

    const { price_range: variantPriceRange } = variant

    const [image, setImage] = useState(
        fullImageUrl(product.small_image?.url, imageParam)
    )

    const getSecondaryImage: () => string | undefined = useCallback(() => {
        const [, secondaryImage] = media_gallery || []
        if (secondaryImage) {
            return fullImageUrl(secondaryImage.url, imageParam) as string
        }

        return
    }, [media_gallery, imageParam])

    const colorsImagesMap = new Map()

    if (isConfig && product.variants) {
        product.variants.map((variant: Variant) => {
            const colorVariant = variant.attributes.find(
                (variant: { code: string }) => variant?.code === swatchAttr
            )
            const img = variant.product.small_image
                ? variant.product.small_image.url
                : false
            if (colorVariant && img) {
                colorsImagesMap.set(colorVariant.label, fullImageUrl(img))
            }
        })
    }

    const addProductHandler = async (
        newSelectedOptions?: SelectedOptions,
        quantity?: number
    ) => {
        // 'variant' of a SimpleProduct is the same as 'product'
        // sample product from casaideas magento to test the checkout page
        // TODO: Remove hardcode below when we have the products coming from the API properly

        let newVariant = variant
        let newSelectedOptionUIDs = selectedOptionUIDs
        if (newSelectedOptions) {
            const variantId = getVariantBySelectedOptions(
                product,
                newSelectedOptions
            )

            newVariant = product?.variants?.[variantId]?.product ?? variant

            const { attributes } = product?.variants?.[variantId] ?? {}

            newSelectedOptionUIDs =
                (attributes as ConfigurableAttributeOption[])?.map(
                    (attr) => attr?.uid ?? ''
                ) ?? []
        }

        const bundleOptions =
            isBundle &&
            product?.items.map(
                (item: BundleItem) =>
                    item.options?.length && item.options[0]?.uid!
            )

        const addToCartOptions = isBundle
            ? bundleOptions
            : newSelectedOptionUIDs

        const response = await addToCart(
            [
                {
                    sku: newVariant?.sku!,
                    uid: newVariant?.uid,
                    ...(newVariant?.sku !== product.sku && {
                        parent_sku: product.sku
                    }),
                    selected_options: addToCartOptions,
                    quantity: quantity ? quantity : 1
                }
            ],
            [product]
        )

        const errorMessage =
            response?.errors?.[0]?.message ??
            response?.user_errors?.[0]?.message ??
            null
        const successMessage = t('common||cart|{{name}} was added to cart', {
            name: product.name
        })

        if (isGiftCardMixCartResult(response)) {
            return
        }

        if (!isMobile) {
            openSidebar()
        }

        if (response?.data && !errorMessage) {
            const { cart } = response.data
            cartUpdateEvent('add', product, cart)
        }

        setTimeout(
            () =>
                isMobile
                    ? openToast(
                          errorMessage ? errorMessage : successMessage,
                          errorMessage ? ToastType.Warning : ToastType.Success,
                          true
                      )
                    : openCartToast(
                          errorMessage ? errorMessage : successMessage,
                          errorMessage ? ToastType.Warning : ToastType.Success
                      ),
            50
        )
    }

    useEffect(() => {
        if (product?.variants?.length > 0) {
            const productVariantIds = product.variants?.map(
                (variant: ConfigurableVariant) => variant?.product?.id
            )

            setIsAddToCartDisabled(!productVariantIds?.includes(variant.id))
        }
    }, [product?.variants, variant])

    useEffect(() => {
        if (options.length && product.variants) {
            const disabledOptions = getDisabledOptionValues(
                options,
                selectedOptions,
                product.variants
            )

            setDisabledOptions(disabledOptions)
        }
    }, [options, product.variants, selectedOptions])

    const InstantAddToCart = () => {
        const { values } = sizeCategoryOption || {}

        const isButtonDisabled =
            isConfig && values
                ? values?.length === 0 && isAddToCartDisabled
                : isAddToCartDisabled

        const showSizeOptions =
            Boolean(hoveredInstantAdd) &&
            Boolean(values) &&
            (values?.length ?? 0) > 0

        return (
            <div
                className={cn(s['productitem-instant-add'], 'reversed-button', {
                    [s['instant-add-with-options']]: showSizeOptions
                })}
            >
                <Button
                    className={cn('btn-add-to-cart-plp-pdp', {
                        'instant-add-with-options': showSizeOptions
                    })}
                    onMouseEnter={() => setHoveredInstantAdd(true)}
                    onMouseLeave={() => setHoveredInstantAdd(false)}
                >
                    {showSizeOptions ? (
                        <div className={s['productitem-instant-add-options']}>
                            {values!.map((switchOption) => {
                                const isDisabled =
                                    disabledOptions?.talla.includes(
                                        switchOption?.label!
                                    )
                                return (
                                    <span
                                        role="button"
                                        tabIndex={isDisabled ? -1 : 0}
                                        className={cn({
                                            [s['size-disabled']]: isDisabled
                                        })}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' &&
                                                !isDisabled
                                            ) {
                                                setSelectedOptions(
                                                    (prevState) => {
                                                        const newSelectedOptions =
                                                            {
                                                                ...prevState,
                                                                talla: switchOption
                                                                    ?.swatch_data
                                                                    ?.value!
                                                            }

                                                        addProductHandler(
                                                            newSelectedOptions
                                                        )
                                                        return newSelectedOptions
                                                    }
                                                )
                                            }
                                        }}
                                        onClick={() => {
                                            if (isDisabled) return
                                            setSelectedOptions((prevState) => {
                                                const newSelectedOptions = {
                                                    ...prevState,
                                                    talla: switchOption
                                                        ?.swatch_data?.value!
                                                }
                                                addProductHandler(
                                                    newSelectedOptions
                                                )
                                                return newSelectedOptions
                                            })
                                        }}
                                        key={switchOption?.value_index}
                                    >
                                        {switchOption?.label}
                                    </span>
                                )
                            })}
                        </div>
                    ) : (
                        <span
                            className={cn({
                                [s['instantadd-button-disabled']]:
                                    isButtonDisabled
                            })}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isButtonDisabled) {
                                    addProductHandler()
                                }
                            }}
                            onClick={() =>
                                !isButtonDisabled ? addProductHandler() : {}
                            }
                        >
                            {isButtonDisabled
                                ? t('common||Out of Stock')
                                : t('common||pdp|Quick add')}
                        </span>
                    )}
                </Button>
            </div>
        )
    }

    // handle variant changes on PDP (FBTProducts)
    useEffect(() => {
        if (isFBT && isConfig && product?.variants) {
            let newSelectedOptionUIDs = selectedOptionUIDs
            if (selectedOptions) {
                const variantId = getVariantBySelectedOptions(
                    product,
                    selectedOptions
                )

                const { attributes } = product?.variants?.[variantId] ?? {}

                newSelectedOptionUIDs =
                    (attributes as ConfigurableAttributeOption[])?.map(
                        (attr) => attr?.uid ?? ''
                    ) ?? []
            }
            handleChangeItemOption(product, newSelectedOptionUIDs)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions])

    if (!product) return null

    const pushProductClickEvent = () => {
        const totalCurrency = formatCurrency(productTotalPrice)

        // Posición en el listado: la restauración al volver atrás apunta a este SKU
        rememberPlpProductClick(product?.sku)

        dataLayerAction({
            type: 'PRODUCT_CLICK',
            data: {
                ...product,
                price: parseFloat(
                    totalCurrency
                        .replace('$', '')
                        .replace('.', '')
                        .replace(',', '.')
                ),
                category: category?.name,
                currencyCode:
                    product.price_range?.minimum_price?.final_price?.currency
            }
        })
    }

    let flagNuevo
    product.categories?.forEach((categoria = Object) => {
        if (categoria.name === 'Nuevos') {
            flagNuevo = 1
        }
    })

    const badgeLabel = flagNuevo ? NEW_FLAG_STRING : badgeImageText
    const badgeText = flag ? flag.label : badgeLabel
    const reviewCountText = `(${review_count})`
    const hasReviews = review_count && review_count > 0

    const reviewStars = hasReviews ? (
        <div className={s['tile-rating-container']}>
            <ReviewStars
                rating={rating_summary}
                mobile={isMobile ? true : false}
            />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            {reviewCountText}
        </div>
    ) : null

    const hasSizeVariants =
        isConfig &&
        sizeCategoryOption &&
        sizeCategoryOption.values &&
        sizeCategoryOption.values.length > 0

    const simpleProductAddToCart = (isMobile || isTablet) && (
        <Button
            onClick={() => addProductHandler()}
            disabled={isAddToCartDisabled}
            className={cn(
                'btn-add-to-cart-plp-pdp',
                s['instant-add-btn-mobile']
            )}
        >
            <h5>
                {isAddToCartDisabled
                    ? t('common||Out of Stock')
                    : t('common||pdp|Quick add')}
            </h5>
        </Button>
    )

    const configurableAddToCart = hasSizeVariants && (
        <ConfigurableAddToCart
            isAddToCartDisabled={isAddToCartDisabled}
            productUrl={productUrl}
            sizeCategoryOption={sizeCategoryOption}
            disabledOptions={disabledOptions}
            product={product}
            addProductHandler={addProductHandler}
        />
    )

    const configProductPrice = (
        <ProductPrice
            className={String(product.__typename).toLowerCase()}
            priceRange={productPriceRange}
            shouldDisplayPriceRange
        />
    )
    const productPrice = (
        <ProductPrice
            className={String(product.__typename).toLowerCase()}
            priceRange={variantPriceRange || productPriceRange}
        />
    )

    const productPriceContainer = !isConfig ? productPrice : configProductPrice

    const mobileAddToCartSelector = hasSizeVariants
        ? configurableAddToCart
        : simpleProductAddToCart

    return (
        <div
            data-product-sku={product?.sku}
            id={product?.sku}
            className={cn(
                s['productitem-block'],
                { show: isVisible },
                { [s['has-custom-text']]: !!customTextAfterProductImage },
                className
            )}
            onMouseLeave={() => setHoveredInstantAdd(false)}
            ref={elementRef}
        >
            <div className={s['productimage-block']}>
                <div className={s['product-badge']}>{badgeText}</div>
                <div aria-hidden onClick={pushProductClickEvent}>
                    <Link
                        href={productUrl}
                        className={cn(s['productimg-link'])}
                        aria-label={product.name}
                        rel="noopener noreferrer"
                    >
                        <img
                            src={image}
                            alt={product.name ? product.name : 'placeholder'}
                            width={318}
                            height={318}
                            className={cn(s['product-img'], 'contain')}
                        />
                        <img
                            src={getSecondaryImage() || image}
                            alt={product.name ? product.name : 'placeholder'}
                            width={318}
                            height={318}
                            className={cn(s['productimg-secondary'], 'contain')}
                        />
                    </Link>
                </div>
                {!isPDP && <InstantAddToCart />}
                <AddToWishlist
                    sku={product?.sku}
                    selected_options={selectedOptionUIDs}
                    quantity={1}
                    isWishlistDisabled={isConfigOptionSelected}
                    isConfig={isConfig}
                    enableRemoveProductFromWishlist
                    buttonContent={renderWishListButtonContent()}
                    errorWithToast
                    stayOnPage
                />
            </div>
            {customTextAfterProductImage && (
                <div className={s['product-custom-text']}>
                    {customTextAfterProductImage}
                </div>
            )}
            {badgeCaptionIcon && (
                <div
                    className={s['product-badge-caption']}
                    dangerouslySetInnerHTML={{
                        __html: badgeCaptionIcon
                    }}
                />
            )}
            <div className={s['product-details']}>
                <div className={s['details-container']}>
                    <h6 className={cn(s['product-name'])}>
                        <div aria-hidden onClick={pushProductClickEvent}>
                            <Link
                                href={productUrl}
                                className={s['productname-link']}
                                title={product.name}
                                rel="noopener noreferrer"
                            >
                                {product.name}
                            </Link>
                        </div>
                    </h6>
                    {reviewStars}

                    {product.product_type_label && (
                        <div className={s['product-type']}>
                            {product.product_type_label}
                        </div>
                    )}

                    {product.model_no && (
                        <div className={s['model-no']}>{product.model_no}</div>
                    )}
                    {productPriceContainer}

                    {isConfig && product.variants && (
                        <VariantSelector
                            disableAll={
                                (product as ConfigurableProduct)
                                    .stock_status ===
                                ProductStockStatus.OutOfStock
                            }
                            variants={product.variants as ConfigurableVariant[]}
                            options={options}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            // @ts-ignore
                            setImage={setImage}
                            colorsImagesMap={colorsImagesMap}
                            displayTallaSwatch={false}
                            displaySwatchLabel={false}
                            restriction={7}
                            productURL={productUrl}
                        />
                    )}
                </div>
                {(isMobile || isTablet) && mobileAddToCartSelector}
            </div>
        </div>
    )
}

export const ProductItem = React.memo(
    (props: ProductTileProps) => <FwrdProductTile {...props} />,
    (prevProps, newProps) =>
        JSON.stringify(prevProps) === JSON.stringify(newProps)
)

ProductItem.displayName = 'MemoProductItem'
